import { ConnectQueryKeys } from '~/connect/api';
import {
  CrudOptions,
  OverridableQuery,
  UseQueryOptions,
  serialize,
  useCrud,
  useQuery,
  useMutation,
  UseMutationOptions,
} from '@wyz/utils';
import { useApi } from '~/common/context';
import {
  CustomerCriterion,
  ICriteriaUsage,
  ICriteriaUsageVariable,
  ISupplierTemplate,
  ISupplierTemplatePayload,
} from '~/connect/types';
import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { isNil } from 'lodash-es';

type Actions = 'create' | 'update' | 'getOneById' | 'remove' | 'patch';

type Options = Pick<
  CrudOptions<
    ISupplierTemplate,
    ISupplierTemplatePayload,
    ISupplierTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<ISupplierTemplate>>,
      IErrorApiResponse
    >
  >;
  getMatched?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<ISupplierTemplate | null>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  > & {
    args: {
      customerCriteria: Array<CustomerCriterion>;
      customerInternalCriteria: Array<CustomerCriterion>;
    };
  };
  checkCriteriaUsage?: UseMutationOptions<
    ISuccessApiResponse<ICriteriaUsage>,
    IErrorApiResponse,
    ICriteriaUsageVariable
  >;
};

export const useManageSupplierTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    Actions,
    ISupplierTemplate,
    ISupplierTemplatePayload,
    ISupplierTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.supplierTemplates.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/supplier-templates`,
    },
    options,
  );

  const listSupplierTemplatesQuery = useQuery({
    ...options.list,
    queryKey: ConnectQueryKeys.supplierTemplates.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<ISupplierTemplate>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/supplier-templates`,
      ),
    enabled: Boolean(options.list?.enabled),
  });

  const matchedSupplierTemplatesQuery = useQuery({
    ...options.getMatched,
    queryKey: [
      ...ConnectQueryKeys.supplierTemplates.base(),
      'match',
      options.getMatched?.args,
    ],
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<ISupplierTemplate | null>>(
        `/tenants/api/wtb/tenants/${tenantCode}/supplier-templates/matched?${serialize(
          options.getMatched?.args,
        )}`,
      ),
    enabled:
      !isNil(options.getMatched?.args?.customerCriteria) &&
      Boolean(options.getMatched?.enabled),

    retry: false,
    gcTime: 0,
  });

  const checkCriteriaUsage = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ICriteriaUsage>>(
        `/tenants/api/wtb/tenants/${tenantCode}/supplier-templates/${
          variables.id
        }/check-usage?${serialize(variables.query)}`,
      ),
    ...options.checkCriteriaUsage,
  });

  return {
    ...api,
    list: listSupplierTemplatesQuery,
    getMatched: matchedSupplierTemplatesQuery,
    checkCriteriaUsage,
  };
};
